import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Users, Target, DollarSign, Clock, Server, CheckCircle, XCircle, AlertCircle, ExternalLink, FileText, MessageSquare, Mail, Send, Tag, Package, UserCheck, Settings, CreditCard, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [brokers, setBrokers] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [brokerRequirements, setBrokerRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBrokers();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, challengesRes, payoutsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getChallenges(),
        adminAPI.getPayouts()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setChallenges(challengesRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/brokers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBrokers(data);
      }
    } catch (error) {
      console.error('Failed to fetch brokers');
    }
  };

  const fetchBrokerRequirements = async (platform) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/brokers/${platform}/requirements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBrokerRequirements(data);
        setSelectedBroker(platform);
      }
    } catch (error) {
      toast.error('Failed to fetch broker requirements');
    }
  };

  const testBrokerConnection = async (platform) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/brokers/test-connection?platform=${platform}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.connected) {
        toast.success(`Connected to ${platform}`);
      } else {
        toast.info(data.message || 'Connection test completed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const handlePayoutStatusChange = async (payoutId, newStatus) => {
    try {
      await adminAPI.updatePayoutStatus(payoutId, newStatus);
      toast.success('Payout status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleChallengeStatusChange = async (challengeId, newStatus) => {
    try {
      await adminAPI.updateChallengeStatus(challengeId, newStatus);
      toast.success('Challenge status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const sendTestWeeklyEmail = async () => {
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/scheduled/send-test-weekly-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.result?.status === 'success') {
        toast.success('Test weekly email sent! Check your inbox.');
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingEmail(false);
    }
  };

  const triggerWeeklyEmails = async () => {
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/scheduled/weekly-emails`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(`Weekly emails sent! ${data.results?.sent || 0} sent, ${data.results?.skipped || 0} skipped`);
    } catch (error) {
      toast.error('Failed to trigger weekly emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      passed: 'bg-primary/20 text-primary border-primary/30',
      failed: 'bg-destructive/20 text-destructive border-destructive/30',
      funded: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-primary/20 text-primary border-primary/30',
      rejected: 'bg-destructive/20 text-destructive border-destructive/30',
      completed: 'bg-primary/20 text-primary border-primary/30'
    };
    return colors[status] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="admin-title">
                Admin <span className="text-primary">Dashboard</span>
              </h1>
              <p className="text-muted-foreground mt-1">Manage users, challenges, and payouts</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate('/admin/crm')}
                data-testid="admin-crm-btn"
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
              >
                <Shield className="w-4 h-4 mr-2" />
                Full CRM
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/packages')}
                data-testid="admin-packages-btn"
                className="hidden sm:flex"
              >
                <Package className="w-4 h-4 mr-2" />
                Packages & Rules
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/affiliates')}
                data-testid="admin-affiliates-btn"
                className="hidden sm:flex"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Affiliates
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/chats')}
                data-testid="admin-chats-btn"
                className="hidden sm:flex"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Chat Support
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/docs')}
                data-testid="admin-docs-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Documentation
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/coupons')}
                data-testid="admin-coupons-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                Coupons
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/settings')}
                data-testid="admin-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Integrations
              </Button>
              <Button 
                variant="default" 
                onClick={() => navigate('/admin/revenue')}
                data-testid="admin-revenue-btn"
                className="bg-green-600 hover:bg-green-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Revenue
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <Card className="bg-card border-border" data-testid="admin-stat-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="font-mono text-3xl font-bold mt-1">{stats?.total_users || 0}</p>
                </div>
                <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="admin-stat-challenges">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Challenges</p>
                  <p className="font-mono text-3xl font-bold mt-1">{stats?.active_challenges || 0}</p>
                </div>
                <div className="w-12 h-12 rounded bg-blue-500/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="admin-stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="font-mono text-3xl font-bold mt-1 text-primary">
                    {formatCurrency(stats?.total_revenue || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="admin-stat-payouts">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Payouts</p>
                  <p className="font-mono text-3xl font-bold mt-1">{stats?.pending_payouts || 0}</p>
                </div>
                <div className="w-12 h-12 rounded bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions: Email Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Weekly Performance Emails</p>
                    <p className="text-xs text-muted-foreground">Send trading summaries to all active traders</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendTestWeeklyEmail}
                    disabled={sendingEmail}
                    data-testid="send-test-email-btn"
                  >
                    {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send Test to Me
                  </Button>
                  <Button
                    size="sm"
                    onClick={triggerWeeklyEmails}
                    disabled={sendingEmail}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="trigger-weekly-emails-btn"
                  >
                    {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send to All Users
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs defaultValue="challenges" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="challenges" data-testid="tab-challenges">Challenges</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
              <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
              <TabsTrigger value="brokers" data-testid="tab-brokers">Brokers</TabsTrigger>
            </TabsList>

            {/* Challenges Tab */}
            <TabsContent value="challenges">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading uppercase">All Challenges</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Package</TableHead>
                          <TableHead>Phase</TableHead>
                          <TableHead>P/L %</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {challenges.map((challenge) => (
                          <TableRow key={challenge.id} data-testid={`challenge-row-${challenge.id}`}>
                            <TableCell className="font-mono text-sm">{challenge.user_id.slice(0, 8)}...</TableCell>
                            <TableCell>{challenge.package_name}</TableCell>
                            <TableCell>Phase {challenge.current_phase}</TableCell>
                            <TableCell className={`font-mono ${challenge.profit_loss_percent >= 0 ? 'text-primary' : 'text-destructive'}`}>
                              {challenge.profit_loss_percent.toFixed(2)}%
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusBadge(challenge.status)}>
                                {challenge.status.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={challenge.status}
                                onValueChange={(value) => handleChallengeStatusChange(challenge.id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="passed">Passed</SelectItem>
                                  <SelectItem value="failed">Failed</SelectItem>
                                  <SelectItem value="funded">Funded</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                        {challenges.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                              No challenges yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading uppercase">All Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Joined</TableHead>
                          <TableHead>First-Time Buyer</TableHead>
                          <TableHead>Admin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                            <TableCell>{user.full_name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{formatDate(user.created_at)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={user.is_first_time_buyer ? 'border-primary/50 text-primary' : ''}>
                                {user.is_first_time_buyer ? 'YES' : 'NO'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={user.is_admin ? 'border-yellow-500/50 text-yellow-400' : ''}>
                                {user.is_admin ? 'ADMIN' : 'USER'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                        {users.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                              No users yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading uppercase">Payout Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payouts.map((payout) => (
                          <TableRow key={payout.id} data-testid={`payout-row-${payout.id}`}>
                            <TableCell className="font-mono text-sm">{payout.user_id.slice(0, 8)}...</TableCell>
                            <TableCell className="font-mono text-primary">{formatCurrency(payout.amount)}</TableCell>
                            <TableCell className="capitalize">{payout.payment_method.replace('_', ' ')}</TableCell>
                            <TableCell>{formatDate(payout.created_at)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusBadge(payout.status)}>
                                {payout.status.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={payout.status}
                                onValueChange={(value) => handlePayoutStatusChange(payout.id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="approved">Approved</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                        {payouts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                              No payout requests yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Brokers Tab */}
            <TabsContent value="brokers">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Broker Platforms */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="font-heading uppercase flex items-center gap-2">
                      <Server className="w-5 h-5 text-primary" />
                      Trading Platforms
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {brokers?.platforms?.map((platform) => (
                        <div 
                          key={platform.platform}
                          className={`p-4 rounded border cursor-pointer transition-all ${
                            selectedBroker === platform.platform 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => fetchBrokerRequirements(platform.platform)}
                          data-testid={`broker-${platform.platform}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-heading font-bold">{platform.name}</h4>
                              <p className="text-xs text-muted-foreground">{platform.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {platform.configured ? (
                                <Badge className="bg-primary/20 text-primary border-primary/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Configured
                                </Badge>
                              ) : platform.platform === 'simulator' ? (
                                <Badge className="bg-primary/20 text-primary border-primary/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Setup Required
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {brokers && (
                      <div className="mt-4 p-3 bg-secondary/30 rounded">
                        <p className="text-sm text-muted-foreground">
                          <strong>Current Platform:</strong>{' '}
                          <span className="text-primary font-mono">{brokers.current_platform}</span>
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Broker Requirements */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="font-heading uppercase">
                      {brokerRequirements ? brokerRequirements.name : 'Select a Platform'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {brokerRequirements ? (
                      <div className="space-y-4">
                        {/* Notes */}
                        <div className="p-3 bg-secondary/30 rounded">
                          <p className="text-sm text-muted-foreground">{brokerRequirements.notes}</p>
                        </div>

                        {/* Credentials Required */}
                        {brokerRequirements.credentials?.length > 0 ? (
                          <div>
                            <h5 className="font-heading text-sm uppercase mb-2">Required Credentials</h5>
                            <div className="space-y-2">
                              {brokerRequirements.credentials.map((cred) => (
                                <div key={cred.key} className="p-3 bg-card border border-border rounded">
                                  <div className="flex justify-between items-center">
                                    <span className="font-mono text-sm">{cred.key}</span>
                                    {cred.required && (
                                      <Badge variant="outline" className="text-xs">Required</Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">{cred.label}</p>
                                  <p className="text-xs text-muted-foreground font-mono">Example: {cred.example}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <CheckCircle className="w-8 h-8 text-primary mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No credentials required</p>
                          </div>
                        )}

                        {/* Recommended Brokers */}
                        {brokerRequirements.recommended_brokers && (
                          <div>
                            <h5 className="font-heading text-sm uppercase mb-2">Recommended Brokers</h5>
                            <div className="flex flex-wrap gap-2">
                              {brokerRequirements.recommended_brokers.map((broker) => (
                                <Badge key={broker} variant="outline">{broker}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Links */}
                        <div className="flex gap-2 pt-2">
                          {brokerRequirements.registration_url && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open(brokerRequirements.registration_url, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Register
                            </Button>
                          )}
                          {brokerRequirements.contact_url && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open(brokerRequirements.contact_url, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Contact
                            </Button>
                          )}
                          <Button 
                            size="sm"
                            onClick={() => testBrokerConnection(selectedBroker)}
                          >
                            Test Connection
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Server className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">Select a platform to view requirements</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Integration Guide Link */}
              <Card className="bg-card border-border mt-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-heading font-bold">Broker Integration Guide</h4>
                      <p className="text-sm text-muted-foreground">
                        Complete documentation on setting up broker partnerships and getting API credentials
                      </p>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => window.open('/docs/BROKER_INTEGRATION_GUIDE.md', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Guide
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
